<template>
  <div class="tabs" :id="id">
    <div :class="navWrapperClass">
      <ul role="tablist"
          class="nav nav-tabs"
          :class="navClass"
          :id="`${id}__BV_tab_controls_`">
        <li v-for="(tab, index) in tabs"
            :key="tab.id"
            role="presentation"
            class="nav-item">
          <a href="#"
             target="_self"
             role="tab"
             class="nav-link"
             :class="{ active: tab.localActive }"
             :id="tab.buttonId"
             :tabindex="tab === focusableTab ? null : -1"
             :aria-selected="tab.localActive ? 'true' : 'false'"
             :aria-setsize="tabs.length"
             :aria-posinset="index + 1"
             :aria-controls="tab.id"
             :ref="el => setButton(tab, el)"
             @click="onButtonEvent(tab, $event)"
             @keydown="onButtonEvent(tab, $event)">{{ tab.title }}</a>
        </li>
      </ul>
    </div>
    <div class="tab-content"
         :class="contentClass"
         :id="`${id}__BV_tab_container_`"
         ref="content">
      <slot/>
      <div v-if="!$slots.default" class="tab-pane active"></div>
    </div>
  </div>
</template>

<script setup>
// Replaces bootstrap-vue's <b-tabs> with the same markup and behaviour (only what the app uses, see
// __tests__/bootstrap/bTabs.test.js). Reference: node_modules/bootstrap-vue/src/components/tabs/tabs.js
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, shallowRef, watch } from "vue"
import { nextId } from "@/components/bootstrap/uid"

defineOptions({
  name: 'BTabs',
})

const props = defineProps({
  // index of the active tab (v-model)
  modelValue: {
    type: Number,
    default: null
  },
  navClass: {
    type: [String, Array, Object],
    default: null
  },
  navWrapperClass: {
    type: [String, Array, Object],
    default: null
  },
  contentClass: {
    type: [String, Array, Object],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'activate-tab'])

const toInteger = (value, fallback) => {
  const integer = parseInt(value, 10)
  return isNaN(integer) ? fallback : integer
}

const id = nextId('tabs')
const content = ref(null)
const currentTab = ref(toInteger(props.modelValue, -1))
// tabs in the order of their panes in the DOM / tabs that have registered, in the order they mounted
const tabs = shallowRef([])
const registeredTabs = shallowRef([])
const buttons = new Map()
let observer = null

// the only tab button reachable with the Tab key: the active one
const focusableTab = computed(() => tabs.value.find(tab => tab.localActive) || tabs.value[0])

const setButton = (tab, el) => {
  if(el) buttons.set(tab, el)
  else buttons.delete(tab)
}

const sortedTabs = () => {
  return registeredTabs.value.slice().sort((a, b) => {
    if(!a.el || !b.el || a.el === b.el) return 0
    return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  })
}

// decides which tab is active after tabs were added, removed or reordered
const updateTabs = () => {
  const list = sortedTabs()
  // the last tab that asks to be active wins
  let tabIndex = list.lastIndexOf(list.slice().reverse().find(tab => tab.localActive))
  if(tabIndex < 0) {
    if(currentTab.value >= list.length) {
      // the active tab was removed from the end: the new last one takes over
      tabIndex = list.length - 1
    } else if(list[currentTab.value]) {
      tabIndex = currentTab.value
    }
  }
  if(tabIndex < 0) tabIndex = list.length ? 0 : -1
  list.forEach((tab, index) => {
    tab.localActive = index === tabIndex
  })
  tabs.value = list
  currentTab.value = tabIndex
}

const activateTab = (tab) => {
  let result = false
  if(tab) {
    const index = tabs.value.indexOf(tab)
    if(index !== currentTab.value && index > -1) {
      const tabEvent = {
        type: 'activate-tab',
        cancelable: true,
        defaultPrevented: false,
        componentId: id,
        preventDefault() {
          this.defaultPrevented = true
        }
      }
      emit('activate-tab', index, currentTab.value, tabEvent)
      if(!tabEvent.defaultPrevented) {
        currentTab.value = index
        result = true
      }
    }
  }
  if(!result && props.modelValue !== currentTab.value) {
    emit('update:modelValue', currentTab.value)
  }
  return result
}

// a tab whose "active" prop was switched off hands over to the first other tab
const deactivateTab = (tab) => {
  return tab ? activateTab(tabs.value.find(other => other !== tab)) : false
}

const focusButton = (tab) => {
  nextTick(() => {
    const button = buttons.get(tab)
    if(button) button.focus()
  })
}

const moveTo = (tab) => {
  if(activateTab(tab)) focusButton(tab)
}

const firstTab = () => moveTo(tabs.value[0])
const lastTab = () => moveTo(tabs.value[tabs.value.length - 1])
const previousTab = () => moveTo(tabs.value[Math.max(currentTab.value, 0) - 1])
const nextTab = () => moveTo(tabs.value[Math.max(currentTab.value, -1) + 1])

const onButtonEvent = (tab, event) => {
  const { type, key, shiftKey } = event
  const stop = () => {
    event.preventDefault()
    event.stopPropagation()
  }
  if(type === 'click' || key === ' ') {
    stop()
    activateTab(tab)
  } else if(['ArrowUp', 'ArrowLeft', 'Home'].includes(key)) {
    stop()
    if(shiftKey || key === 'Home') firstTab()
    else previousTab()
  } else if(['ArrowDown', 'ArrowRight', 'End'].includes(key)) {
    stop()
    if(shiftKey || key === 'End') lastTab()
    else nextTab()
  }
}

provide('bvTabs', {
  registerTab(tab) {
    if(!registeredTabs.value.includes(tab)) registeredTabs.value = [...registeredTabs.value, tab]
  },
  unregisterTab(tab) {
    registeredTabs.value = registeredTabs.value.filter(other => other !== tab)
  },
  activateTab,
  deactivateTab,
})

watch(registeredTabs, updateTabs)

watch(currentTab, (index) => {
  tabs.value.forEach((tab, i) => {
    tab.localActive = i === index
  })
  emit('update:modelValue', index)
})

watch(() => props.modelValue, (newValue, oldValue) => {
  if(newValue === oldValue) return
  newValue = toInteger(newValue, -1)
  oldValue = toInteger(oldValue, 0)
  const tab = tabs.value[newValue]
  if(tab) {
    activateTab(tab)
  } else if(newValue < oldValue) {
    activateTab(tabs.value[Math.max(currentTab.value, 0) - 1])
  } else {
    activateTab(tabs.value[Math.max(currentTab.value, -1) + 1])
  }
})

onMounted(() => {
  // tabs reordered in place (v-for with keys) do not register again
  observer = new MutationObserver(() => {
    nextTick(() => requestAnimationFrame(updateTabs))
  })
  observer.observe(content.value, { childList: true, subtree: false, attributes: true, attributeFilter: ['id'] })
})

onBeforeUnmount(() => {
  if(observer) observer.disconnect()
  observer = null
  tabs.value = []
})
</script>
